import { useRef, useEffect, ReactNode } from 'react';
import { KioskLocation } from '../../types/assign-kiosk-types';
import { useAssignKioskStore } from '../../stores/assign-kiosk-store';
import { ConnectorTable } from './ConnectorTable';

interface LocationAccordionRowProps {
  location: KioskLocation;
  hideSwitch?: boolean;
  outlined?: boolean;
  footerContent?: ReactNode;
  headerRight?: ReactNode;
  headerAction?: ReactNode;
}

/**
 *
 * Layout (matches ControlledExpandableTile + OcpiSwitch):
 *
 *   [▼ chevron]  Location Name                [OcpiSwitch]
 *                (evseCount) - LocationId (id)
 *   ─────────────────────────────────────────────────────
 *   [ConnectorTable]
 *
 * - Chevron (left): clicking the tile body expands/collapses, independent of selection.
 * - OcpiSwitch (right, 70×31, yellow active): toggles selection ON/OFF; selecting auto-expands.
 * - Disabled switch (greyed out) + orange subtitle when location has no EVSEs.
 * - Selected location gets white card with rounded border (Flutter: parentChecked → white bg).
 */
export function NicknameValidationWarning({ location }: { location: KioskLocation }) {
  const { screenType } = useAssignKioskStore();
  if (screenType !== 'SPARK_DEVICE_START') return null;
  const fieldName = 'Label';
  const selected = location.evses
    .flatMap((e) => e.connectors.map((c) => ({ evseUid: e.uid, alias: c.alias.trim(), isSelected: c.isSelected })))
    .filter((c) => c.isSelected);

  // Check for duplicate labels
  const nonEmpty = selected.filter((c) => c.alias !== '').map((c) => c.alias);
  const hasDuplicates = nonEmpty.length !== new Set(nonEmpty).size;
  if (hasDuplicates) {
    return (
      <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <span className="mr-2">⚠</span>
        Duplicate {fieldName}s detected. Each name must be unique.
      </div>
    );
  }

  // Check for missing labels (only if at least one has a label)
  if (!selected.some((c) => c.alias !== '')) return null;
  const missing = selected.filter((c) => c.alias === '').map((c) => c.evseUid);
  if (missing.length === 0) return null;
  return (
    <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      <span className="mr-2">⚠</span>
      If any EVSE has a {fieldName}, then ALL selected EVSEs must have one.
    </div>
  );
}

export function LocationAccordionRow({ location, hideSwitch = false, outlined = false, footerContent, headerRight, headerAction }: LocationAccordionRowProps) {
  const { selectedLocationId, expandedLocationId, screenType, toggleLocationSwitch, setExpandedLocation } =
    useAssignKioskStore();
  const isSparkType = screenType === 'SPARK_DEVICE_START' || screenType === 'SPARK_REMOTE_START';

  const isSelected = selectedLocationId === location.id;
  const isExpanded = expandedLocationId === location.id;
  const hasEvses = location.evses.length > 0;
  const evseCount = location.evses.length;
  const rowRef = useRef<HTMLDivElement>(null);

  // Scroll into view when this location becomes expanded/selected
  useEffect(() => {
    if (isExpanded && rowRef.current) {
      rowRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [isExpanded]);

  const handleTileClick = () => {
    if (!hasEvses) return;
    setExpandedLocation(isExpanded ? null : location.id);
  };

  const handleSwitchClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!hasEvses) return;
    toggleLocationSwitch(location.id);
  };

  return (
    <div
      ref={rowRef}
      className={outlined
        ? 'rounded-lg border border-gray-200 bg-white overflow-hidden'
        : `mb-1 rounded-md transition-colors ${isSelected ? 'bg-white shadow-sm ring-1 ring-gray-200' : 'bg-transparent'}`}
    >
      {/* ── Tile header ──────────────────────────────────────────────────── */}
      <div
        onClick={handleTileClick}
        className={`flex items-start gap-2 px-4 py-3 ${hasEvses ? 'cursor-pointer hover:bg-gray-50/70 rounded-md' : 'cursor-default'}`}
      >
        {/* Chevron (left) — down when expanded, right when collapsed */}
        <span
          className={`shrink-0 mt-0.5 text-gray-500 transition-transform duration-200 ${
            isExpanded ? 'rotate-0' : '-rotate-90'
          } ${!hasEvses ? 'invisible' : ''}`}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </span>

        {/* Location name + subtitle */}
        <div className="flex flex-1 flex-col min-w-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-base font-medium text-gray-900 truncate">{location.name}</span>
            {headerRight && (
              <div onClick={(e) => e.stopPropagation()} className="shrink-0 flex items-center">
                {headerRight}
              </div>
            )}
          </div>
          {(hasEvses ? (
            <span className="text-sm text-gray-500">
              ({evseCount}) - LocationId ({location.id})
            </span>
          ) : (
            <span className="text-sm italic text-orange-500">
              No charge points available - LocationId ({location.id})
            </span>
          ))}
        </div>

        {headerAction && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0 mt-0.5">
            {headerAction}
          </div>
        )}
        {!hideSwitch && <button
          role="switch"
          aria-checked={isSelected}
          onClick={handleSwitchClick}
          disabled={!hasEvses}
          className="shrink-0 mt-0.5 focus:outline-none"
          style={{ width: 44, height: 24 }}
        >
          <div
            style={{ width: 44, height: 24, borderRadius: 12 }}
            className={`relative transition-colors duration-300
              ${isSelected ? 'bg-yellow-400' : 'bg-gray-300'}
              ${!hasEvses ? 'opacity-50' : ''}`}
          >
            {/* Thumb */}
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                top: 2,
                transition: 'left 0.3s ease-in-out',
                left: isSelected ? 44 - 20 - 2 : 2,
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              }}
              className="absolute bg-white"
            />
          </div>
        </button>}
      </div>

      {/* ── Expanded connector table ──────────────────────────────────────── */}
      {isExpanded && hasEvses && (
        <div className="px-4 pb-4 pt-1">
          <ConnectorTable location={location} />
        </div>
      )}

      {/* ── Optional footer ────────────── */}
      {footerContent && footerContent}
    </div>
  );
}
